import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { 
  Calendar, Users, Trophy, MapPin, Clock, ChevronRight, 
  Plus, Minus, Check, AlertCircle, QrCode, Download,
  Waves, CircleDot, Footprints, Flag, Table2, Map,
  User, Baby, UserPlus, CreditCard, ArrowLeft, ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity icon mapping
const activityIcons = {
  waves: Waves,
  circle: CircleDot,
  footprints: Footprints,
  flag: Flag,
  table: Table2,
  map: Map,
  'circle-dot': CircleDot,
};

// Default participant type styling (can be overridden by config)
const participantTypeStyles = {
  primary: { icon: User, color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-600' },
  adult: { icon: Users, color: 'blue', bgClass: 'bg-blue-100', textClass: 'text-blue-600' },
  junior: { icon: Baby, color: 'amber', bgClass: 'bg-amber-100', textClass: 'text-amber-600' },
  guest: { icon: UserPlus, color: 'purple', bgClass: 'bg-purple-100', textClass: 'text-purple-600' },
  guest_child: { icon: Baby, color: 'pink', bgClass: 'bg-pink-100', textClass: 'text-pink-600' },
  guest_adult: { icon: UserPlus, color: 'purple', bgClass: 'bg-purple-100', textClass: 'text-purple-600' },
};

// Karen Country Club Family Sports Day Hero Image
const KCC_HERO_IMAGE = 'https://customer-assets-cm19k8pv.emergentagent.net/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/ccmnkaam_IMG-20260806-WA0046%282%29.webp';

// Demo/Fallback data - defined outside component to avoid recreation on each render
const DEMO_EVENT = {
  id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
  name: 'Karen Country Club Family Sports Day 2026',
  description: 'A fun-filled day of sports, games, family bonding, and friendly competition!',
  start_date: '2026-08-29T08:00:00',
  end_date: '2026-08-29T18:00:00',
  registration_deadline: '2026-08-25T23:59:00',
  max_registrations: 200,
  current_registrations: 0,
  image_url: KCC_HERO_IMAGE,
  venue: 'Karen Country Club'
};

const DEMO_PARTICIPANT_TYPES = [
  { type: 'primary', label: 'Primary Member', fee: 0, description: 'Club member (free entry)', enabled: true },
  { type: 'adult', label: 'Adult Family', fee: 0, description: 'Spouse, parent, or adult family (free)', enabled: true },
  { type: 'junior', label: 'Junior (Under 18)', fee: 0, description: 'Children under 18 (free)', enabled: true },
  { type: 'guest_child', label: 'Guest Child', fee: 500, description: 'Non-member children', enabled: true },
  { type: 'guest_adult', label: 'Guest Adult', fee: 1000, description: 'Non-member adults', enabled: true },
];

const DEMO_FORM_FIELDS = [
  { field: 'first_name', label: 'First Name', type: 'text', required: true, enabled: true, for_types: ['all'] },
  { field: 'last_name', label: 'Last Name', type: 'text', required: true, enabled: true, for_types: ['all'] },
  { field: 'date_of_birth', label: 'Date of Birth', type: 'date', required: false, enabled: true, for_types: ['junior'] },
  { field: 'age', label: 'Age', type: 'number', required: false, enabled: true, for_types: ['all'] },
  { field: 'gender', label: 'Gender', type: 'select', required: false, enabled: true, for_types: ['all'], options: ['male', 'female', 'other'] },
  { field: 'email', label: 'Email', type: 'email', required: false, enabled: true, for_types: ['adult', 'guest'] },
  { field: 'phone', label: 'Phone', type: 'tel', required: false, enabled: true, for_types: ['adult', 'guest'] },
  { field: 'relationship', label: 'Relationship to Primary', type: 'select', required: false, enabled: true, for_types: ['all'], options: ['self', 'spouse', 'child', 'parent', 'sibling', 'friend', 'colleague'] },
  { field: 'team', label: 'Team', type: 'select', required: false, enabled: true, for_types: ['all'], options: ['Golfers', 'Gymkhana', 'Karenites', 'Juniors'] },
  { field: 'emergency_contact_name', label: 'Emergency Contact Name', type: 'text', required: false, enabled: true, for_types: ['all'] },
  { field: 'emergency_contact_phone', label: 'Emergency Contact Phone', type: 'tel', required: false, enabled: true, for_types: ['all'] },
  { field: 'medical_notes', label: 'Medical Notes', type: 'textarea', required: false, enabled: true, for_types: ['all'] },
  { field: 'dietary_restrictions', label: 'Dietary Restrictions', type: 'textarea', required: false, enabled: false, for_types: ['all'] },
];

// Activities from the KCC Family Sports Day flyer - ALL FREE
const DEMO_ACTIVITIES = [
  { id: '1', name: 'Football Tournament', sport: 'Football', activity_type: 'team', scheduled_time: '09:00 AM', location: 'Football Pitch', min_participants: 5, max_participants: 11, fee_per_team: 0, icon: 'circle-dot', color: '#EF4444', description: 'Team football tournament. Compete against other families!' },
  { id: '2', name: 'Touch Rugby', sport: 'Rugby', activity_type: 'team', scheduled_time: '09:30 AM', location: 'Main Field', min_participants: 6, max_participants: 8, fee_per_team: 0, icon: 'circle', color: '#8B5CF6', description: 'Non-contact rugby for all skill levels.' },
  { id: '3', name: '4 x 4 Relay Race', sport: 'Athletics', activity_type: 'team', scheduled_time: '10:00 AM', location: 'Track', min_participants: 4, max_participants: 4, fee_per_team: 0, icon: 'footprints', color: '#F59E0B', description: '4-person relay race. Speed and teamwork!' },
  { id: '4', name: 'Full Relay Race', sport: 'Athletics', activity_type: 'team', scheduled_time: '10:30 AM', location: 'Track', min_participants: 6, max_participants: 8, fee_per_team: 0, icon: 'footprints', color: '#22C55E', description: 'Extended relay with more runners.' },
  { id: '5', name: 'Tug of War', sport: 'Strength', activity_type: 'team', scheduled_time: '11:00 AM', location: 'Main Field', min_participants: 6, max_participants: 10, fee_per_team: 0, icon: 'circle', color: '#DC2626', description: 'Classic tug of war showdown!' },
  { id: '6', name: 'Sack Race', sport: 'Fun Games', activity_type: 'individual', scheduled_time: '11:30 AM', location: 'Main Field', min_participants: 1, max_participants: 1, fee_per_participant: 0, icon: 'footprints', color: '#10B981', description: 'Hop your way to victory in the sack race!' },
  { id: '7', name: 'Obstacle Race', sport: 'Adventure', activity_type: 'individual', scheduled_time: '12:00 PM', location: 'Obstacle Course', min_participants: 1, max_participants: 1, fee_per_participant: 0, icon: 'map', color: '#0EA5E9', description: 'Navigate through challenging obstacles.' },
  { id: '8', name: 'Egg & Spoon Race', sport: 'Fun Games', activity_type: 'individual', scheduled_time: '12:30 PM', location: 'Main Field', min_participants: 1, max_participants: 1, fee_per_participant: 0, icon: 'circle', color: '#F97316', description: 'Balance and speed - carry the egg to the finish!' },
  { id: '9', name: 'Water Bottle Refill Challenge', sport: 'Fun Games', activity_type: 'team', scheduled_time: '01:00 PM', location: 'Activity Area', min_participants: 2, max_participants: 4, fee_per_team: 0, icon: 'waves', color: '#06B6D4', description: 'Team relay to refill water bottles fastest!' },
  { id: '10', name: 'Bouncing Castle', sport: 'Kids Fun', activity_type: 'individual', scheduled_time: 'All Day', location: 'Kids Zone', min_participants: 1, max_participants: 1, fee_per_participant: 0, icon: 'circle', color: '#EC4899', description: 'Free bouncing castle fun for kids!' },
  { id: '11', name: 'Trampoline', sport: 'Kids Fun', activity_type: 'individual', scheduled_time: 'All Day', location: 'Kids Zone', min_participants: 1, max_participants: 1, fee_per_participant: 0, icon: 'circle', color: '#A855F7', description: 'Bounce high on the trampoline!' },
  { id: '12', name: 'Family Games', sport: 'Fun Games', activity_type: 'family', scheduled_time: '02:00 PM', location: 'Club Grounds', min_participants: 3, max_participants: 6, fee_per_team: 0, icon: 'map', color: '#14B8A6', description: 'Various family-friendly games and activities.' },
];

export default function FamilySportsDayPage() {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Info, 2: Participants, 3: Activities, 4: Review, 5: Confirmation
  
  // Configuration state (loaded from API or defaults)
  const [participantTypes, setParticipantTypes] = useState(DEMO_PARTICIPANT_TYPES);
  const [formFields, setFormFields] = useState(DEMO_FORM_FIELDS);
  const [teamOptions, setTeamOptions] = useState(['Golfers', 'Gymkhana', 'Karenites', 'Juniors']);
  const [branding, setBranding] = useState({
    hero_image_url: DEMO_EVENT.image_url,
    primary_color: '#16a34a',
    secondary_color: '#059669'
  });
  
  // Registration state
  const [registration, setRegistration] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '' });
  
  // Form state
  const [newParticipant, setNewParticipant] = useState({
    first_name: '',
    last_name: '',
    participant_type: 'adult',
    team: '',
    date_of_birth: '',
    age: '',
    gender: '',
    relationship: '',
    email: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    medical_notes: '',
    dietary_restrictions: ''
  });
  const [showParticipantForm, setShowParticipantForm] = useState(false);
  
  // Fetch event data and configuration
  const fetchEventData = useCallback(async () => {
    setLoading(true);
    
    // Start with demo defaults - will be overridden by API data if successful
    let fetchedEvent = DEMO_EVENT;
    let fetchedActivities = DEMO_ACTIVITIES;
    let fetchedParticipantTypes = DEMO_PARTICIPANT_TYPES;
    let fetchedFormFields = DEMO_FORM_FIELDS;
    let fetchedTeamOptions = ['Golfers', 'Gymkhana', 'Karenites', 'Juniors'];
    let fetchedBranding = {
      hero_image_url: DEMO_EVENT.image_url,
      primary_color: '#16a34a',
      secondary_color: '#059669'
    };
    
    try {
      // Try to fetch from API
      const eventsRes = await fetch(`${API_URL}/api/events`);
      
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        const kccEvent = eventsData.find(e => e.name?.includes('KCC') || e.name?.includes('Family Sports Day'));
        
        if (kccEvent) {
          fetchedEvent = kccEvent;
          
          // Fetch full configuration for this event
          const configRes = await fetch(`${API_URL}/api/events/${kccEvent.id}/config`);
          if (configRes.ok) {
            const configData = await configRes.json();
            
            // Update event with full details
            if (configData.event) {
              fetchedEvent = { ...fetchedEvent, ...configData.event };
            }
            
            // Update activities
            if (configData.activities && configData.activities.length > 0) {
              fetchedActivities = configData.activities;
            }
            
            // Update participant types
            if (configData.participant_types && configData.participant_types.length > 0) {
              fetchedParticipantTypes = configData.participant_types;
            }
            
            // Update team options from config
            if (configData.team_options && configData.team_options.length > 0) {
              fetchedTeamOptions = configData.team_options;
            }
            
            // Update form fields
            if (configData.form_fields && configData.form_fields.length > 0) {
              fetchedFormFields = configData.form_fields;
            }
            
            // Update branding
            if (configData.branding) {
              fetchedBranding = {
                hero_image_url: configData.branding.hero_image_url || fetchedEvent.image_url || DEMO_EVENT.image_url,
                primary_color: configData.branding.primary_color || '#16a34a',
                secondary_color: configData.branding.secondary_color || '#059669'
              };
            }
          } else {
            // Fallback to just activities if config endpoint fails
            const activitiesRes = await fetch(`${API_URL}/api/events/${kccEvent.id}/activities`);
            if (activitiesRes.ok) {
              const activitiesData = await activitiesRes.json();
              if (activitiesData && activitiesData.length > 0) {
                fetchedActivities = activitiesData;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      // Demo data already set as default
    }
    
    // Set state with either API data or demo fallbacks
    setEvent(fetchedEvent);
    setActivities(fetchedActivities);
    setParticipantTypes(fetchedParticipantTypes);
    setTeamOptions(fetchedTeamOptions);
    setFormFields(fetchedFormFields);
    setBranding(fetchedBranding);
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);
  
  // Helper to get participant type config
  const getParticipantTypeConfig = (type) => {
    const pt = participantTypes.find(p => p.type === type);
    const style = participantTypeStyles[type] || participantTypeStyles.guest;
    return {
      ...style,
      label: pt?.label || type,
      fee: pt?.fee || 0,
      description: pt?.description || ''
    };
  };
  
  // Check if a form field should be shown for a participant type
  const shouldShowField = (field, participantType) => {
    if (!field.enabled) return false;
    if (field.for_types.includes('all')) return true;
    return field.for_types.includes(participantType);
  };
  
  // Add participant
  const handleAddParticipant = () => {
    if (!newParticipant.first_name || !newParticipant.last_name) {
      toast.error('Please enter first and last name');
      return;
    }
    
    const config = getParticipantTypeConfig(newParticipant.participant_type);
    const participant = {
      ...newParticipant,
      id: `temp-${Date.now()}`,
      fee_amount: config.fee
    };
    
    setParticipants([...participants, participant]);
    setNewParticipant({
      first_name: '',
      last_name: '',
      participant_type: 'adult',
      team: '',
      date_of_birth: '',
      age: '',
      gender: '',
      relationship: '',
      email: '',
      phone: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      medical_notes: '',
      dietary_restrictions: ''
    });
    setShowParticipantForm(false);
    toast.success('Participant added');
  };
  
  // Remove participant
  const handleRemoveParticipant = (index) => {
    const updated = [...participants];
    updated.splice(index, 1);
    setParticipants(updated);
  };
  
  // Toggle activity selection
  const toggleActivity = (activity) => {
    const isSelected = selectedActivities.find(a => a.id === activity.id);
    if (isSelected) {
      setSelectedActivities(selectedActivities.filter(a => a.id !== activity.id));
    } else {
      setSelectedActivities([...selectedActivities, { ...activity, team_name: '', participant_ids: [] }]);
    }
  };
  
  // Update activity team
  const updateActivityTeam = (activityId, field, value) => {
    setSelectedActivities(selectedActivities.map(a => 
      a.id === activityId ? { ...a, [field]: value } : a
    ));
  };
  
  // Calculate total
  const calculateTotal = () => {
    let total = 0;
    
    // Participant fees
    participants.forEach(p => {
      total += p.fee_amount || 0;
    });
    
    // Activity fees
    selectedActivities.forEach(a => {
      if (a.fee_per_team) total += parseFloat(a.fee_per_team);
      if (a.fee_per_participant && a.participant_ids?.length) {
        total += parseFloat(a.fee_per_participant) * a.participant_ids.length;
      }
    });
    
    return total;
  };
  
  // Submit registration
  const handleSubmit = async () => {
    try {
      // Create registration
      const registrationData = {
        event_id: event.id,
        contact_email: contactInfo.email,
        contact_phone: contactInfo.phone,
        participants: participants.map(p => ({
          first_name: p.first_name,
          last_name: p.last_name,
          participant_type: p.participant_type,
          team: p.team || null,
          date_of_birth: p.date_of_birth || null,
          age: p.age ? parseInt(p.age) : null,
          gender: p.gender || null,
          relationship: p.relationship || null,
          email: p.email || null,
          phone: p.phone || null,
          emergency_contact_name: p.emergency_contact_name || null,
          emergency_contact_phone: p.emergency_contact_phone || null,
          medical_notes: p.medical_notes || null,
          dietary_restrictions: p.dietary_restrictions || null
        }))
      };
      
      const userId = user?.id || '00000000-0000-0000-0000-000000000001';
      const res = await fetch(`${API_URL}/api/events/${event.id}/registrations?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });
      
      if (res.ok) {
        const regData = await res.json();
        setRegistration(regData);
        
        // Add activity assignments
        for (const activity of selectedActivities) {
          await fetch(`${API_URL}/api/registrations/${regData.id}/activities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              activity_id: activity.id,
              team_name: activity.team_name || `${participants[0]?.last_name || 'Team'} Family`,
              participant_ids: activity.participant_ids || []
            })
          });
        }
        
        // Update registration status
        await fetch(`${API_URL}/api/registrations/${regData.id}?status=pending`, {
          method: 'PUT'
        });
        
        setStep(5);
        toast.success('Registration submitted successfully!');
      } else {
        const errorData = await res.json().catch(() => ({ detail: 'Failed to submit registration' }));
        throw new Error(errorData.detail || 'Failed to submit registration');
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Show error to user - don't silently fall back to demo mode
      toast.error(error.message || 'Registration failed. Please try again.');
      
      // Only use demo mode if explicitly enabled via URL param
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('demo') === 'true' || urlParams.get('test_mode') === 'true') {
        setRegistration({
          id: 'demo-reg',
          registration_number: `KCC-2026-${Math.floor(Math.random() * 9000) + 1000}`,
          qr_code: 'MNET-REG-KCC-2026-DEMO',
          total_amount: calculateTotal(),
          participants: participants.map(p => ({ ...p, qr_code: `MNET-PART-${p.id}` }))
        });
        setStep(5);
        toast.info('Demo Mode: Registration preview only');
      }
    }
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return renderEventInfo();
      case 2:
        return renderParticipants();
      case 3:
        return renderActivities();
      case 4:
        return renderReview();
      case 5:
        return renderConfirmation();
      default:
        return null;
    }
  };
  
  // Step 1: Event Info
  const renderEventInfo = () => (
    <div className="space-y-8">
      {/* Hero - Mobile: show flyer with minimal overlay, Desktop: full overlay */}
      <div className="relative rounded-2xl overflow-hidden">
        {/* Mobile: Just show the flyer image cleanly */}
        <div className="md:hidden">
          <img 
            src={branding.hero_image_url || event?.image_url || DEMO_EVENT.image_url} 
            alt="Family Sports Day"
            className="w-full h-auto object-contain rounded-2xl"
          />
          {/* Mobile text below the image */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 rounded-b-2xl -mt-2">
            <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full mb-2">
              Registration Open
            </span>
            <h1 className="text-xl font-bold text-white mb-1">
              {event?.name || 'KCC Family Sports Day 2026'}
            </h1>
            <p className="text-green-100 text-sm">
              {event?.description || 'Join us for a fun-filled day of sports and activities for the whole family!'}
            </p>
          </div>
        </div>
        
        {/* Desktop: Original overlay design */}
        <div className="hidden md:block h-[400px]">
          <img 
            src={branding.hero_image_url || event?.image_url || DEMO_EVENT.image_url} 
            alt="Family Sports Day"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-10">
            <span 
              className="inline-block px-3 py-1 text-white text-sm font-medium rounded-full mb-3"
              style={{ backgroundColor: branding.primary_color }}
            >
              Registration Open
            </span>
            <h1 className="text-5xl font-bold text-white mb-2 drop-shadow-lg">
              {event?.name || 'KCC Family Sports Day 2026'}
            </h1>
            <p className="text-gray-200 text-lg max-w-2xl drop-shadow-md">
              {event?.description || 'Join us for a fun-filled day of sports and activities for the whole family!'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Event Details */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border shadow-sm flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <Calendar className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Date</p>
            <p className="font-semibold text-gray-900">
              {event?.start_date 
                ? new Date(event.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : 'August 29, 2026'}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border shadow-sm flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Clock className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Time</p>
            <p className="font-semibold text-gray-900">8:00 AM - 6:00 PM</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border shadow-sm flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <MapPin className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Venue</p>
            <p className="font-semibold text-gray-900">{event?.venue || 'Karen Country Club'}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border shadow-sm flex items-start gap-4">
          <div className="p-3 bg-amber-100 rounded-lg">
            <Trophy className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Activities</p>
            <p className="font-semibold text-gray-900">{activities.length} Sports</p>
          </div>
        </div>
      </div>
      
      {/* Pricing */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border border-green-100">
        <h2 className="text-xl font-bold mb-4 text-gray-900">Registration Fees</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {participantTypes.filter(pt => pt.enabled !== false).map((pt) => {
            const style = participantTypeStyles[pt.type] || participantTypeStyles.guest;
            const Icon = style.icon;
            return (
              <div key={pt.type} className="bg-white rounded-xl p-4 text-center border">
                <Icon className={`w-8 h-8 mx-auto mb-2 ${style.textClass}`} />
                <p className="font-medium text-gray-900">{pt.label}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pt.fee === 0 ? 'FREE' : `KES ${pt.fee}`}
                </p>
                {pt.description && (
                  <p className="text-xs text-gray-500 mt-1">{pt.description}</p>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-sm text-gray-600 mt-4 text-center">
          * Activity fees are additional and vary per activity
        </p>
      </div>
      
      {/* Activities Preview */}
      {activities.length > 0 && (
        <div>
          <h2 className="text-xl font-bold mb-4 text-gray-900">Available Activities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activities.slice(0, 6).map(activity => {
              const IconComponent = activityIcons[activity.icon] || Trophy;
              return (
                <div key={activity.id} className="bg-white rounded-xl p-5 border shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${activity.color}20` }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: activity.color }} />
                    </div>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full capitalize">
                      {activity.activity_type}
                    </span>
                  </div>
                  <h3 className="font-semibold mb-1 text-gray-900">{activity.name}</h3>
                  <p className="text-sm text-gray-500 mb-2">{activity.sport}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {activity.scheduled_time}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {activity.location}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
      
      {/* CTA */}
      <div className="text-center">
        <Button 
          size="lg" 
          onClick={() => setStep(2)}
          className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
          data-testid="start-registration-btn"
        >
          Start Registration
          <ChevronRight className="ml-2 w-5 h-5" />
        </Button>
        <p className="text-sm text-gray-500 mt-3">
          Registration deadline: {event?.registration_deadline 
            ? new Date(event.registration_deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
            : 'August 25, 2026'}
        </p>
      </div>
    </div>
  );
  
  // Step 2: Participants
  const renderParticipants = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Add Participants</h2>
          <p className="text-gray-600">Add family members and guests who will participate</p>
        </div>
        <Button 
          onClick={() => setShowParticipantForm(true)}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-participant-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Participant
        </Button>
      </div>
      
      {/* Participant Form Modal */}
      <AnimatePresence>
        {showParticipantForm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowParticipantForm(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4">Add Participant</h3>
              
              <div className="space-y-4">
                {/* Participant Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Participant Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {participantTypes.filter(pt => pt.enabled !== false).map((pt) => (
                      <button
                        key={pt.type}
                        onClick={() => setNewParticipant({ ...newParticipant, participant_type: pt.type })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          newParticipant.participant_type === pt.type 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-gray-900">{pt.label}</p>
                        <p className="text-sm text-gray-500">
                          {pt.fee === 0 ? 'Free' : `KES ${pt.fee}`}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={newParticipant.first_name}
                      onChange={e => setNewParticipant({ ...newParticipant, first_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="participant-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={newParticipant.last_name}
                      onChange={e => setNewParticipant({ ...newParticipant, last_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="participant-last-name"
                    />
                  </div>
                </div>
                
                {/* Relationship & Age */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <select
                      value={newParticipant.relationship}
                      onChange={e => setNewParticipant({ ...newParticipant, relationship: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select...</option>
                      {(formFields.find(f => f.field === 'relationship')?.options || 
                        ['self', 'spouse', 'child', 'parent', 'sibling', 'friend', 'colleague']).map(r => (
                        <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <input
                      type="number"
                      value={newParticipant.age}
                      onChange={e => setNewParticipant({ ...newParticipant, age: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      min="1"
                      max="100"
                    />
                  </div>
                </div>
                
                {/* Gender */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    value={newParticipant.gender}
                    onChange={e => setNewParticipant({ ...newParticipant, gender: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                {/* Team Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team *</label>
                  <select
                    value={newParticipant.team}
                    onChange={e => setNewParticipant({ ...newParticipant, team: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="participant-team-select"
                  >
                    <option value="">Select your team...</option>
                    {teamOptions.map(team => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Choose which team you'll compete for</p>
                </div>
                
                {/* Contact */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newParticipant.email}
                      onChange={e => setNewParticipant({ ...newParticipant, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={newParticipant.phone}
                      onChange={e => setNewParticipant({ ...newParticipant, phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
                
                {/* Emergency Contact */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Emergency Contact</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newParticipant.emergency_contact_name}
                        onChange={e => setNewParticipant({ ...newParticipant, emergency_contact_name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newParticipant.emergency_contact_phone}
                        onChange={e => setNewParticipant({ ...newParticipant, emergency_contact_phone: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Medical Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medical Notes</label>
                  <textarea
                    value={newParticipant.medical_notes}
                    onChange={e => setNewParticipant({ ...newParticipant, medical_notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows={2}
                    placeholder="Allergies, conditions, medications..."
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowParticipantForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddParticipant}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="confirm-add-participant"
                >
                  Add Participant
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Participants List */}
      {participants.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center border-2 border-dashed">
          <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600">No participants added yet</p>
          <p className="text-sm text-gray-500">Add yourself and family members to continue</p>
        </div>
      ) : (
        <div className="space-y-3">
          {participants.map((participant, index) => {
            const config = getParticipantTypeConfig(participant.participant_type);
            const Icon = config?.icon || User;
            return (
              <div key={participant.id} className="bg-white rounded-xl p-4 border flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${config?.bgClass || 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${config?.textClass || 'text-gray-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{participant.first_name} {participant.last_name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{config?.label}</span>
                      {participant.team && (
                        <>
                          <span>•</span>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            {participant.team}
                          </span>
                        </>
                      )}
                      {participant.relationship && (
                        <>
                          <span>•</span>
                          <span>{participant.relationship}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-medium">
                    {participant.fee_amount === 0 ? 'FREE' : `KES ${participant.fee_amount}`}
                  </span>
                  <button 
                    onClick={() => handleRemoveParticipant(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Contact Info */}
      <div className="bg-white rounded-xl p-6 border">
        <h3 className="font-semibold mb-4 text-gray-900">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={contactInfo.email}
              onChange={e => setContactInfo({ ...contactInfo, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="your@email.com"
              data-testid="contact-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={contactInfo.phone}
              onChange={e => setContactInfo({ ...contactInfo, phone: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="+254 7XX XXX XXX"
              data-testid="contact-phone"
            />
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={() => setStep(3)}
          disabled={participants.length === 0}
          className="bg-green-600 hover:bg-green-700"
          data-testid="continue-to-activities"
        >
          Continue to Activities
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
  
  // Step 3: Activities
  const renderActivities = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Select Activities</h2>
        <p className="text-gray-600">Choose activities for your family to participate in</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activities.map(activity => {
          const isSelected = selectedActivities.find(a => a.id === activity.id);
          const IconComponent = activityIcons[activity.icon] || Trophy;
          
          return (
            <div 
              key={activity.id}
              className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all ${
                isSelected ? 'border-green-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => toggleActivity(activity)}
              data-testid={`activity-${activity.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${activity.color}20` }}
                >
                  <IconComponent className="w-6 h-6" style={{ color: activity.color }} />
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
              
              <h3 className="font-semibold mb-1 text-gray-900">{activity.name}</h3>
              <p className="text-sm text-gray-500 mb-3">{activity.description}</p>
              
              <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-3">
                <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                  <Clock className="w-3 h-3" />
                  {activity.scheduled_time}
                </span>
                <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                  <MapPin className="w-3 h-3" />
                  {activity.location}
                </span>
                <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                  <Users className="w-3 h-3" />
                  {activity.min_participants}-{activity.max_participants} players
                </span>
              </div>
              
              <div className="pt-3 border-t flex justify-between items-center">
                <span className="text-sm text-gray-500 capitalize">{activity.activity_type}</span>
                <span className="font-semibold text-green-600">
                  KES {activity.fee_per_team || activity.fee_per_participant || 0}
                </span>
              </div>
              
              {/* Team name input when selected */}
              {isSelected && (
                <div className="mt-4 pt-4 border-t" onClick={e => e.stopPropagation()}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                  <input
                    type="text"
                    value={isSelected.team_name || ''}
                    onChange={e => updateActivityTeam(activity.id, 'team_name', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder={`${participants[0]?.last_name || 'Your'} Family`}
                    data-testid={`team-name-${activity.id}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={() => setStep(4)}
          className="bg-green-600 hover:bg-green-700"
          data-testid="continue-to-review"
        >
          Review Registration
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
  
  // Step 4: Review
  const renderReview = () => {
    const total = calculateTotal();
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Review Your Registration</h2>
          <p className="text-gray-600">Please verify all details before submitting</p>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-xl p-5 border border-green-100">
            <p className="text-sm text-green-600 mb-1">Total Participants</p>
            <p className="text-3xl font-bold text-green-700">{participants.length}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <p className="text-sm text-blue-600 mb-1">Activities Selected</p>
            <p className="text-3xl font-bold text-blue-700">{selectedActivities.length}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
            <p className="text-sm text-amber-600 mb-1">Total Amount</p>
            <p className="text-3xl font-bold text-amber-700">KES {total.toLocaleString()}</p>
          </div>
        </div>
        
        {/* Participants */}
        <div className="bg-white rounded-xl p-6 border">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Participants ({participants.length})
          </h3>
          <div className="divide-y">
            {participants.map(p => (
              <div key={p.id} className="py-3 flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{p.first_name} {p.last_name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{getParticipantTypeConfig(p.participant_type)?.label}</span>
                    {p.team && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {p.team}
                      </span>
                    )}
                  </div>
                </div>
                <p className="font-medium">KES {p.fee_amount}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Activities */}
        {selectedActivities.length > 0 && (
          <div className="bg-white rounded-xl p-6 border">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Activities ({selectedActivities.length})
            </h3>
            <div className="divide-y">
              {selectedActivities.map(a => (
                <div key={a.id} className="py-3 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{a.name}</p>
                    <p className="text-sm text-gray-500">
                      Team: {a.team_name || `${participants[0]?.last_name || 'Your'} Family`}
                    </p>
                  </div>
                  <p className="font-medium">KES {a.fee_per_team || a.fee_per_participant || 0}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Contact */}
        <div className="bg-white rounded-xl p-6 border">
          <h3 className="font-semibold mb-4 text-gray-900">Contact Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Email</p>
              <p className="font-medium">{contactInfo.email || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-gray-500">Phone</p>
              <p className="font-medium">{contactInfo.phone || 'Not provided'}</p>
            </div>
          </div>
        </div>
        
        {/* Total */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-green-100">Total Amount Due</p>
              <p className="text-4xl font-bold">KES {total.toLocaleString()}</p>
            </div>
            <CreditCard className="w-12 h-12 text-green-200" />
          </div>
        </div>
        
        {/* Terms */}
        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
          <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5" />
          <p className="text-sm text-gray-600">
            By submitting this registration, you agree to the event terms and conditions. 
            Payment can be made via M-Pesa or at the venue on the event day.
          </p>
        </div>
        
        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep(3)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700 px-8"
            data-testid="submit-registration"
          >
            Submit Registration
            <Check className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  };
  
  // Step 5: Confirmation
  const renderConfirmation = () => (
    <div className="text-center space-y-8">
      {/* Success Icon */}
      <motion.div 
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center"
      >
        <Check className="w-12 h-12 text-green-600" />
      </motion.div>
      
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
        <p className="text-gray-600">
          Your registration for KCC Family Sports Day has been submitted
        </p>
      </div>
      
      {/* Registration Number */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 max-w-md mx-auto">
        <p className="text-sm text-gray-600 mb-2">Registration Number</p>
        <p className="text-3xl font-bold text-green-700 font-mono">
          {registration?.registration_number || 'KCC-2026-XXXX'}
        </p>
        <div className="mt-6 flex justify-center">
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <QrCode className="w-32 h-32 text-gray-800" />
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          Show this QR code at the venue for check-in
        </p>
      </div>
      
      {/* Summary */}
      <div className="bg-white rounded-xl p-6 border max-w-md mx-auto text-left">
        <h3 className="font-semibold mb-4 text-gray-900">Registration Summary</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Participants</span>
            <span className="font-medium">{participants.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Activities</span>
            <span className="font-medium">{selectedActivities.length}</span>
          </div>
          <div className="flex justify-between border-t pt-3">
            <span className="text-gray-500">Total Amount</span>
            <span className="font-bold text-green-600">KES {calculateTotal().toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Payment Status</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
              Pending
            </span>
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Download QR Codes
        </Button>
        <Button 
          className="bg-green-600 hover:bg-green-700 gap-2"
          onClick={() => window.location.href = '/'}
        >
          Back to Home
        </Button>
      </div>
      
      <p className="text-sm text-gray-500">
        A confirmation email has been sent to {contactInfo.email || 'your email'}
      </p>
    </div>
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="family-sports-day-page">
      {/* Progress Steps */}
      {step < 5 && (
        <div className="bg-white border-b sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              {['Event Info', 'Participants', 'Activities', 'Review'].map((label, index) => (
                <div key={label} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step > index + 1 
                      ? 'bg-green-500 text-white' 
                      : step === index + 1 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > index + 1 ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <span className={`ml-2 text-sm hidden sm:inline ${
                    step >= index + 1 ? 'text-gray-900 font-medium' : 'text-gray-400'
                  }`}>
                    {label}
                  </span>
                  {index < 3 && (
                    <div className={`w-8 sm:w-16 h-0.5 mx-2 ${
                      step > index + 1 ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
